import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of, Subject } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import moment from 'moment';
import * as _ from 'lodash';
import { ActiveToast, ToastrService } from 'ngx-toastr';

import { AppConfig } from './../../../app.settings';
import { RequestLicenseModel } from '../../models/view-models/license-models/request-license.model';
import { LicenseInfoModel } from '../../models/view-models/license-models/license-info.model';
import { LicenseStatusModel } from '../../models/view-models/license-models/license-status.model';
import { AppConstants } from '../../../app.constants';
import { LicenseDialogMessagesEnum } from '../../enums/toastr-messages/license-dialog-messages.enum';

@Injectable(
  { providedIn: 'root' }
)
export class LicenseService {

  public licenseStatusModel$: BehaviorSubject<LicenseStatusModel> = new BehaviorSubject(null);
  public licenseInfoModel: BehaviorSubject<LicenseInfoModel> = new BehaviorSubject(null);
  public isAllowToInnitFeature: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isShowUserLicenseNotification: Subject<boolean> = new Subject();
  public isShowUserUpgradeLicenseNotification: Subject<boolean> = new Subject();
  public updatelicenseInfo: Subject<void> = new Subject();
  public licenseStatusModel: LicenseStatusModel = null;
  public isStatusInPending: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isNavigateToAdmin: boolean = false;

  private endpoint: string;
  private allowedFeatures: string[] = [];
  private passwordExpirationWarningToast: ActiveToast<any>;

  constructor(
    private readonly http: HttpClient,
    private readonly config: AppConfig,
    private readonly toastrService: ToastrService,
    private readonly router: Router
  ) {
    this.endpoint = `${this.config.getConfig('api')}licenses`;
  }

  activateLicense(key: string): Observable<boolean> {
    const params = new HttpParams().set('licenseKey', key);

    return this.http
      .post<boolean>(`${this.endpoint}/activate`, null, { params }).pipe(
        tap(
          () => {
            this.initLicenseStatusSubscription(true);
            this.updatelicenseInfo.next();
          },
          () => this.onActivateError())
      );
  }

  activateLicenseFromFile(file: File): Observable<boolean> {
    this.licenseInfoModel.next(null);
    this.updatelicenseInfo.next();

    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', '*/*');

    return this.http
      .post<boolean>(`${this.endpoint}/activate-file`, formData, { headers }).pipe(
        tap(
          () => this.initLicenseStatusSubscription(true),
          () => this.onActivateError())
      );
  }

  generateLicenseRequest(requestLicenseModel: RequestLicenseModel): Observable<Blob> {
    return this.http.post<Blob>(`${this.endpoint}/request`, requestLicenseModel,
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), responseType: 'blob' as 'json' });
  }

  initLicenseAdditionalInfo(licenseModel: LicenseStatusModel): void {
    if (licenseModel.IsLicensed && licenseModel.Status) {
      this.getLicenseInfo().subscribe();
    }
  }

  initLicenseStatusSubscription(checkModalWindowToShow: boolean = false): void {
    if (checkModalWindowToShow) {
      localStorage.removeItem(AppConstants.IS_NOTIFICATION_WINDOW_CLOSED);
    }
    this.getLicenseStatus()
      .pipe(
        tap(data => {
          if (data?.IsPasswordExpired) {
            this.showPasswordExpirationWarning();
          }
        })
      )
      .subscribe();
  }

  getLicenseAllowedFeatures(): Observable<string[]> {
    return this.http.get<string[]>(`${this.endpoint}/allowed-features`).pipe(
      map((allowedFeatures: string[]) => {
        this.allowedFeatures = allowedFeatures;
        return allowedFeatures;
      })
    );
  }

  generateOSBaseLicenseKeys(physicalAddress: string): Observable<void> {
    return this.http.get<void>(`${this.endpoint}/generate-osbase-license-keys/${physicalAddress}`);
  }

  downloadOsbaseLicenseFile(fileName: string): Observable<any> {
    const params = new HttpParams().set('fileName', fileName);

    const req: HttpRequest<any> = new HttpRequest('GET', `${this.endpoint}/download-osbase-file`, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      params,
      observe: 'response',
      responseType: 'blob' as 'json',
      reportProgress: true
    });

    return this.http.request(req);
  }

  activateOSBaseLicenseFile(file: File, physicalAddress: string): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', '*/*');

    const req: HttpRequest<any> = new HttpRequest('POST', `${this.endpoint}/activate-osbase-license/${physicalAddress}`, formData, {
      headers,
      reportProgress: true
    });

    return this.http.request(req);
  }

  public isAllowedByLicense(featurename: string, checkAllowedByTrialMode: boolean = false): boolean {
    const allowedByFeature: boolean = this.allowedFeatures && this.allowedFeatures.length &&
      _.some(this.allowedFeatures, (allowedFeatureName: string) => allowedFeatureName.toLowerCase().includes(featurename.toLowerCase()));
    const allowedByTrialMode: boolean = checkAllowedByTrialMode && this.licenseStatusModel
      && this.licenseStatusModel.Status && !this.licenseStatusModel.IsLicensed;

    return allowedByFeature || allowedByTrialMode;
  }

  public checkIsToShowNotification(licenseStatusModel: LicenseStatusModel): void {
    const isNotificationWindowClosed: boolean = JSON.parse(localStorage.getItem(AppConstants.IS_NOTIFICATION_WINDOW_CLOSED));
    const lastDateChecked: string = localStorage.getItem(AppConstants.NOTIFICATION_LAST_DATE_CLOSED);
    const notificationShowDate: string = localStorage.getItem(AppConstants.NOTIFICATION_SHOW_DATE);

    if (!isNotificationWindowClosed || (!lastDateChecked && !notificationShowDate)) {
      this.setNotificationValue(licenseStatusModel);
    } else if (lastDateChecked) {
      const lastDateFormatted: string = moment(new Date(lastDateChecked)).format('YYYY/MM/D');
      const currentDate: string = moment(new Date()).format('YYYY/MM/D');

      if (lastDateFormatted && new Date(currentDate) > new Date(lastDateFormatted)) {
        this.setNotificationValue(licenseStatusModel);
      }
    } else if (notificationShowDate) {
      const showNotificationDate: string = moment(new Date(notificationShowDate)).format('YYYY/MM/D');
      const currentDate: string = moment(new Date()).format('YYYY/MM/D');

      if (showNotificationDate && (new Date(currentDate) >= new Date(showNotificationDate))) {
        this.setNotificationValue(licenseStatusModel);
      }
    }
  }

  public removePasswordExpirationWarning(): void {
    if (this.passwordExpirationWarningToast?.toastId) {
      this.toastrService.clear(this.passwordExpirationWarningToast.toastId);
    }
  }

  private getLicenseInfo(): Observable<LicenseInfoModel> {
    return this.http.get<LicenseInfoModel>(`${this.endpoint}/info`).pipe(
      map((licenseInfoModel: LicenseInfoModel) => {
        this.licenseInfoModel.next(licenseInfoModel);
        return licenseInfoModel;
      })
    );
  }

  private getLicenseStatus(): Observable<LicenseStatusModel> {
    return this.http.get<LicenseStatusModel>(`${this.endpoint}/status`).pipe(
      map((licenseStatusModel: LicenseStatusModel) => {
        this.licenseStatusModel$.next(licenseStatusModel);
        this.licenseStatusModel = licenseStatusModel;
        this.allowedFeatures = licenseStatusModel.AllowedFeatures;
        if (window.location.hash === '#/') {
          this.checkIsToShowNotification(licenseStatusModel);
        }
        return licenseStatusModel;
      }),
      catchError(() => {
        const licenseStatusModel: LicenseStatusModel = null;
        this.licenseStatusModel$.next(licenseStatusModel);
        this.licenseStatusModel = licenseStatusModel;
        this.allowedFeatures = [];
        return of(licenseStatusModel);
      })
    );
  }

  private onActivateError(): void {
    this.updatelicenseInfo.error(null);
    this.licenseStatusModel$.next(null);
    this.licenseStatusModel = null;
    this.getLicenseStatus().subscribe();
  }

  private setNotificationValue(licenseStatusModel: LicenseStatusModel): void {
    this.getLicenseInfo()
      .pipe(take(1))
      .subscribe(licenseInfoModel => {
        if (licenseStatusModel.IsLicensed && licenseStatusModel.Status && licenseStatusModel.IsVersionUpgrade) {
          this.isShowUserUpgradeLicenseNotification.next(true);
        }
        else if (licenseStatusModel.IsLicensed && licenseStatusModel.Status && licenseInfoModel.SupportExpirationDate &&
          (licenseStatusModel.LicenseSupportDaysLeft <= 1 || licenseStatusModel.LicenseSupportDaysLeft === 15
            || licenseStatusModel.LicenseSupportDaysLeft === 30)) {
          this.isShowUserLicenseNotification.next(true);
        }
      });
  }

  private showPasswordExpirationWarning(): void {
    this.passwordExpirationWarningToast = this.toastrService.warning(
      LicenseDialogMessagesEnum.PasswordExpiredMessage,
      LicenseDialogMessagesEnum.PasswordExpiredTitle,
      AppConstants.TOASTR_CONFIG
    );

    this.passwordExpirationWarningToast.onTap
      .pipe(
        take(1)
      )
      .subscribe(
        () => this.router.navigateByUrl('/Profile/ChangePassword')
      );
  }
}
